import { useMemo } from "react";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Typography from "core/components/Typography";
import { ROLES } from "core/constants";
import useActiveMembership from "core/hooks/useActiveMembership";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";

import { useOrganizationByIdQuery } from "core/queries";
import DetailsViewer from "modules/admin/DetailsViewer";
import OrgPreferencesForm from "modules/admin/OrgPreferencesForm";
import { updateOrganization } from "modules/admin/actions";
import { toast } from "sonner";

const OrganizationPreferencesPage = () => {
  const { orgId: organizationId } = useParams();
  const queryClient = useQueryClient();
  const { activeMembership } = useActiveMembership();

  const { data: organization, isLoading } = useOrganizationByIdQuery({
    organizationId,
  });

  const defaultValues = useMemo(
    () => ({
      preferences: {
        bi_portal: {
          default_states:
            organization?.preferences?.bi_portal?.default_states?.[0] || "",
        },
      },
    }),
    [organization]
  );

  const updateOrg = useAuthenticatedMutation((req) =>
    updateOrganization({ ...req, organizationId })
  );

  const orgMutation = useMutation({
    mutationFn: updateOrg,
    onSuccess: (_, updatedOrg) => {
      queryClient.setQueryData(
        ["organizations", { organizationId }],
        (cachedOrg) => {
          if (!cachedOrg) return cachedOrg;
          return {
            ...cachedOrg,
            ...updatedOrg,
            isUpdated: true,
            resetUpdateParams: {
              queryKey: ["organizations", { organizationId }],
              rowId: organizationId,
            },
          };
        }
      );
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Preferences have been updated.");
    },
  });

  const handleSubmit = (formData) => {
    orgMutation.mutate({
      preferences: {
        bi_portal: {
          default_states: [formData.preferences.bi_portal.default_states],
        },
      },
    });
  };

  const canEdit = activeMembership.role === ROLES.systemAdmin;

  return (
    <div style={{ maxWidth: "500px" }}>
      <Typography variant="h2">Preferences</Typography>

      {canEdit ? (
        <OrgPreferencesForm
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
          isFetching={isLoading}
          isSubmitting={orgMutation.isPending}
          error={orgMutation.error}
          submitLabel="Save Changes"
        />
      ) : (
        <DetailsViewer
          isLoading={isLoading}
          data={[
            {
              label: "Default State",
              value:
                organization?.preferences?.bi_portal?.default_states?.[0] ||
                "—",
            },
          ]}
        />
      )}
    </div>
  );
};

export default OrganizationPreferencesPage;
